import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, CheckCircle2, Reply } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { useNavigate } from "react-router-dom";

// Booking-style 1–10 rating UI. Read-only renders a colored badge ("8.4"); editable shows pill selector.
const SCORE_LABEL = (v) => {
    if (v >= 9) return "Olağanüstü";
    if (v >= 8) return "Çok İyi";
    if (v >= 7) return "İyi";
    if (v >= 6) return "Memnun Edici";
    if (v >= 4) return "Orta";
    if (v > 0) return "Zayıf";
    return "Puanlanmamış";
};
const SCORE_COLOR = (v) => {
    if (v >= 8) return "bg-emerald-600 text-white";
    if (v >= 6) return "bg-emerald-700/80 text-white";
    if (v >= 4) return "bg-amber-600/90 text-white";
    if (v > 0) return "bg-rose-600/80 text-white";
    return "bg-emerald_deep text-muted_green border border-border_green";
};

export const ScoreBadge = ({ value, size = "md", showLabel = true }) => {
    const v = Number(value) || 0;
    const sz = size === "sm" ? "px-1.5 py-0.5 text-[11px]" : size === "lg" ? "px-3 py-2 text-2xl font-bold" : "px-2 py-1 text-sm font-semibold";
    return (
        <div className="inline-flex items-center gap-2">
            <span className={`${SCORE_COLOR(v)} ${sz} rounded-sm font-mono`}>{v ? v.toFixed(1) : "—"}</span>
            {showLabel && v > 0 && <span className="text-xs text-muted_green">{SCORE_LABEL(v)}</span>}
        </div>
    );
};

const RatingPicker = ({ value, onChange }) => (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="rating-picker">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
            const active = n === value;
            return (
                <button
                    key={n}
                    type="button"
                    onClick={() => onChange?.(n)}
                    className={`w-9 h-9 text-sm font-mono transition-all border ${
                        active
                            ? `${SCORE_COLOR(n)} border-transparent scale-110 shadow-lg`
                            : "bg-emerald_surface border-border_green text-bone/70 hover:border-gold hover:text-gold"
                    }`}
                    data-testid={`rating-${n}`}
                    aria-label={`${n} puan`}
                >
                    {n}
                </button>
            );
        })}
        {value > 0 && (
            <span className="text-xs text-muted_green ml-2" data-testid="rating-label">{SCORE_LABEL(value)}</span>
        )}
    </div>
);

const HotelReviews = ({ hotelId }) => {
    const { settings, lang } = useApp();
    const { customer } = useCustomerAuth();
    const navigate = useNavigate();
    const [data, setData] = useState({ reviews: [], count: 0, average_rating: 0 });
    const [eligibility, setEligibility] = useState({ can_review: false, reason: null, checked: false });
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({ customer_name: "", rating: 8, comment: "", customer_phone: "" });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/hotels/${hotelId}/reviews`);
            setData(data);
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    }, [hotelId]);

    useEffect(() => { if (hotelId) load(); }, [hotelId, load]);

    // Check eligibility and auto-fill form for logged-in customers
    useEffect(() => {
        if (!hotelId) return;
        if (!customer) { setEligibility({ can_review: false, reason: "Yorum yapmak için üye girişi yapmalısınız", checked: true }); return; }
        api.get(`/customer/can-review/${hotelId}`)
            .then(({ data }) => setEligibility({ ...data, checked: true }))
            .catch(() => setEligibility({ can_review: false, reason: "Doğrulanamadı", checked: true }));
        setForm((f) => ({
            ...f,
            customer_name: f.customer_name || customer.name || "",
            customer_phone: f.customer_phone || customer.phone || "",
        }));
    }, [hotelId, customer]);

    const handleOpenForm = () => {
        if (!customer) {
            toast.info("Yorum yapmak için üye girişi yapmalısınız");
            navigate("/login");
            return;
        }
        if (!eligibility.can_review) {
            toast.error(eligibility.reason || "Yorum yapamazsınız");
            return;
        }
        setShowForm(!showForm);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (form.customer_name.trim().length < 2) { toast.error("İsim gerekli"); return; }
        if (form.comment.trim().length < 5) { toast.error("Yorumunuz çok kısa"); return; }
        setSubmitting(true);
        try {
            await api.post("/reviews", { hotel_id: hotelId, ...form });
            setSubmitted(true);
            setForm({ customer_name: "", rating: 8, comment: "", customer_phone: "" });
            toast.success("Yorumunuz alındı. Onaylandıktan sonra yayınlanacak.");
            setTimeout(() => { setShowForm(false); setSubmitted(false); }, 2500);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Gönderilemedi");
        } finally { setSubmitting(false); }
    };

    return (
        <div data-testid="hotel-reviews">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <h2 className="font-serif text-3xl">Konuk Yorumları</h2>
                    {data.count > 0 && (
                        <div className="flex items-center gap-3 mt-2">
                            <ScoreBadge value={data.average_rating} size="lg" />
                            <span className="text-sm text-muted_green">/ 10 · {data.count} yorum</span>
                        </div>
                    )}
                </div>
                <button
                    onClick={handleOpenForm}
                    className="btn-outline-gold !text-xs"
                    data-testid="toggle-review-form-btn"
                >
                    <MessageSquare className="w-3.5 h-3.5" /> {showForm ? "Kapat" : "Yorum Yaz"}
                </button>
            </div>

            <AnimatePresence>
                {showForm && (
                    <motion.form
                        onSubmit={submit}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border border-gold/40 bg-emerald_surface p-6 space-y-4 mb-8 overflow-hidden"
                        data-testid="review-form"
                    >
                        {submitted ? (
                            <div className="text-center py-6">
                                <CheckCircle2 className="w-12 h-12 text-gold mx-auto mb-3" />
                                <div className="font-serif text-xl mb-1">Teşekkürler!</div>
                                <div className="text-sm text-muted_green">Yorumunuz onay sürecinden sonra yayınlanacaktır.</div>
                            </div>
                        ) : (
                            <>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="label-dark">Adınız</label>
                                        <input
                                            className="input-dark"
                                            value={form.customer_name}
                                            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                                            placeholder="Adınız soyadınız"
                                            data-testid="review-name-input"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="label-dark">Puanınız (1–10)</label>
                                        <div className="pt-2">
                                            <RatingPicker value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="label-dark">Yorumunuz</label>
                                    <textarea
                                        className="input-dark"
                                        rows={4}
                                        value={form.comment}
                                        onChange={(e) => setForm({ ...form, comment: e.target.value })}
                                        placeholder="Konaklama deneyiminizi paylaşın..."
                                        maxLength={1200}
                                        data-testid="review-comment-input"
                                        required
                                    />
                                    <div className="text-[10px] text-muted_green text-right mt-1">{form.comment.length}/1200</div>
                                </div>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-gold w-full sm:w-auto"
                                    data-testid="submit-review-btn"
                                >
                                    {submitting ? "Gönderiliyor…" : "Yorumu Gönder"}
                                </button>
                                <div className="text-[11px] text-muted_green">
                                    Yorumlar yayınlanmadan önce yönetici tarafından gözden geçirilir.
                                </div>
                            </>
                        )}
                    </motion.form>
                )}
            </AnimatePresence>

            {loading ? (
                <div className="text-muted_green text-sm">Yükleniyor…</div>
            ) : data.reviews.length === 0 ? (
                <div className="border border-dashed border-border_green p-10 text-center" data-testid="reviews-empty-state">
                    <MessageSquare className="w-10 h-10 text-gold/60 mx-auto mb-3" />
                    <div className="font-serif text-xl mb-1">Henüz yorum yok</div>
                    <div className="text-sm text-muted_green">İlk yorumu siz yazın ve diğer misafirlere yardımcı olun.</div>
                </div>
            ) : (
                <div className="space-y-3">
                    {data.reviews.map((r, i) => (
                        <motion.div
                            key={r.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: i * 0.04 }}
                            className="border border-border_green bg-emerald_surface p-5"
                            data-testid={`review-card-${r.id}`}
                        >
                            <div className="flex items-start justify-between flex-wrap gap-2">
                                <div>
                                    <div className="font-serif text-lg">{r.customer_name}</div>
                                    <div className="text-[11px] text-muted_green mt-0.5">
                                        {new Date(r.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                                    </div>
                                </div>
                                <ScoreBadge value={r.rating} size="sm" showLabel={false} />
                            </div>
                            <p className="mt-3 text-sm leading-relaxed text-bone/90 whitespace-pre-wrap">
                                {lang === "en" && r.comment_en ? r.comment_en : r.comment}
                            </p>
                            {lang === "en" && r.comment_en && (
                                <div className="mt-1 text-[10px] text-muted_green italic" data-testid={`translated-note-${r.id}`}>
                                    Automatically translated from Turkish
                                </div>
                            )}

                            {r.admin_reply && (
                                <div
                                    className="mt-4 ml-3 sm:ml-6 border-l-2 border-gold/50 pl-4 bg-emerald_deep/40 p-3"
                                    data-testid={`admin-reply-${r.id}`}
                                >
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <Reply className="w-3.5 h-3.5 text-gold" />
                                        <span className="font-serif text-sm text-gold">
                                            {settings?.company_name || "Asis Hotels"}
                                        </span>
                                        <span className="text-[10px] uppercase tracking-[0.18em] text-bone/60">
                                            · Otelin Cevabı
                                        </span>
                                        {r.admin_reply_at && (
                                            <span className="text-[10px] text-muted_green">
                                                · {new Date(r.admin_reply_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm leading-relaxed text-bone/85 whitespace-pre-wrap">
                                        {lang === "en" && r.admin_reply_en ? r.admin_reply_en : r.admin_reply}
                                    </p>
                                </div>
                            )}
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default HotelReviews;
